import { Gem, HeartHandshake, Truck } from "lucide-react";
import { ChapterLabel, MaskReveal, Reveal } from "@/components/Reveal";
import { ADDRESS } from "@/lib/constants";

const VALUES = [
  {
    title: "Craft",
    text: "Trained hands, honest methods and an obsession with the perfect finish.",
    icon: Gem,
  },
  {
    title: "Care",
    text: "Every fabric is treated for what it is — never rushed, never guessed.",
    icon: HeartHandshake,
  },
  {
    title: "Convenience",
    text: "Door-to-door pickup and delivery arranged in moments over WhatsApp.",
    icon: Truck,
  },
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="relative bg-[#0E1420] py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-5">
            <ChapterLabel index="03" title="About Us" testId="chapter-about" tone="dark" />
            <h2 className="mt-8 font-display text-4xl font-light leading-[1.08] tracking-tight text-[#F9F6F0] sm:text-5xl">
              <MaskReveal>A standard of care</MaskReveal>
              <MaskReveal delay={0.12}>
                <em className="gold-text-bright font-medium italic">your wardrobe deserves.</em>
              </MaskReveal>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-10">
            <Reveal delay={0.1}>
              <p className="text-base font-light leading-relaxed tracking-wide text-[#A8B0BC] sm:text-lg">
                R&J Dry Cleaners is a premium garment-care house on the High Street
                in Yiewsley, West Drayton. We built our name on a simple promise:
                treat every garment as if it were our own — from the everyday
                shirt to the once-in-a-lifetime gown.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base font-light leading-relaxed tracking-wide text-[#A8B0BC] sm:text-lg">
                Our team combines traditional pressing and finishing craft with
                modern, fabric-safe cleaning methods. And with WhatsApp booking,
                doorstep pickup and reliable delivery, impeccable care has never
                asked less of your day.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <blockquote className="mt-12 border-l-2 border-[#D4AF37]/60 pl-7">
                <p className="font-display text-2xl font-light italic leading-snug text-[#F9F6F0] sm:text-[1.7rem]">
                  “No garment leaves our hands until it is perfect.”
                </p>
                <cite className="mt-4 block text-[11px] font-medium uppercase not-italic tracking-[0.35em] text-[#D4AF37]">
                  The R&J Standard
                </cite>
              </blockquote>
            </Reveal>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-1 gap-px border border-[#D4AF37]/15 bg-[#D4AF37]/15 sm:grid-cols-3">
          {VALUES.map((value, i) => {
            const Icon = value.icon;
            return (
              <Reveal key={value.title} delay={0.1 * i} className="h-full">
                <div
                  data-testid={`value-card-${value.title.toLowerCase()}`}
                  className="group flex h-full flex-col bg-[#151C2A] p-10 transition-colors duration-500 hover:bg-[#1A2334]"
                >
                  <Icon className="h-6 w-6 text-[#D4AF37]" strokeWidth={1.25} />
                  <h3 className="mt-7 font-display text-2xl font-medium text-[#F9F6F0]">
                    {value.title}
                  </h3>
                  <p className="mt-3 text-sm font-light leading-relaxed tracking-wide text-[#A8B0BC]">
                    {value.text}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15} className="mt-16">
          <p className="flex items-center gap-4 text-xs uppercase tracking-[0.3em] text-[#A8B0BC]">
            <span className="h-px w-10 bg-[#D4AF37]/40" />
            Find us at {ADDRESS}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
